import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";
import AuditChangesModal from "./AuditChangesModal";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { USERS_RETRIEVE } from "../../auth/permissions";
import Audit from "../../models/audit";
import { formatDateTime } from "../../utils/dates";

const ITEMS_PER_PAGE = 10;

const AuditCrud = () => {
  const fields = [
    {
      key: "date",
      _classes: ["text-center"],
      label: "Fecha",
      sorter: true,
    },
    {
      key: "entityName",
      _classes: ["text-center"],
      label: "Entidad",
      sorter: true,
    },
    {
      key: "userEmail",
      _classes: ["text-center"],
      label: "Usuario",
      sorter: true,
    },
    {
      key: "operation",
      _classes: ["text-center"],
      label: "Operación",
      sorter: true,
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<string>("");
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | undefined>(
    undefined
  );
  const history = useHistory();

  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchAudits = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);

    const auditsStatus = await getList<Audit>(
      `/company_audits/${companyId}/audit_logs/`,
      limit,
      offset,
      urlParams
    );

    if (auditsStatus.status === SUCCESS) {
      if (auditsStatus.data !== undefined) {
        const count = auditsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setAudits(auditsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = auditsStatus.detail
        ? auditsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onViewChangesClick = (audit: Audit) => {
    setSelectedAudit(audit);
    setShowChangesModal(true);
  };

  const onCloseChangesModal = () => {
    setSelectedAudit(undefined);
    setShowChangesModal(false);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    urlParams.set("page", "1");
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchAudits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("audit")) {
        return;
      }
      fetchAudits();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Registro de Auditoría</h3>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Buscar por usuario, entidad o acción"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={audits}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    date: (item: Audit) => (
                      <td>{formatDateTime(new Date(item.date))}</td>
                    ),
                    entity_name: (item: Audit) => <td>{item.entityName}</td>,
                    user_email: (item: Audit) => (
                      <td>{item.userEmail || "Sistema"}</td>
                    ),
                    initiator: (item: Audit) => <td>{item.initiator}</td>,
                    actions: (item: Audit) => {
                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            <PermissionRequiredComponent
                              permissionName={USERS_RETRIEVE}
                            >
                              <CButton
                                className="text-white"
                                color="info"
                                onClick={() => onViewChangesClick(item)}
                              >
                                <i className="fa fa-eye"></i>
                              </CButton>
                            </PermissionRequiredComponent>
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Modal to display changes */}
      <AuditChangesModal
        show={showChangesModal}
        onClose={onCloseChangesModal}
        audit={selectedAudit}
      />
    </>
  );
};

export default AuditCrud;
